import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { exportCsv, queryList } from "./model";
import { searchErrorHandler } from "utils/formValidation";
import s from "./OfflinePayments.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import moment from "moment";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import cardTypeOptions from "utils/cardTypeOptions/CardTypeOptions.jsx";
import offlinePaymentStatusOptions from "utils/paymentStatusOptions/OfflinePaymentStatusOptions.js";
import cardOriginOperationOptions from "utils/cardOriginOperationOptions/CardOriginOperationOptions.jsx";
import transactions from "routes/transactions";
import { PortalContext } from "App";
import paymentGatewayOptionsOperationOffline from "utils/paymentGatewayOptionsOperationOffline/PaymentGatewayOptionsOperationOffline.jsx";

const PAGE_LOCALE_KEY = "page.transactions.offlinePayments.";

const INPUT_FIELD_CREATE_STATE = () => ({
  id: "",
  employeeId: "",
  merchantId: "",
  startDate: moment().subtract(1, "month").startOf("day").toISOString(),
  endDate: moment().endOf("day").toISOString(),
  cardType: "",
  fromAmount: "",
  toAmount: "",
  status: "",
  cardOrigin: "",
  gateway: "",
});

const INPUT_FIELD_ID_OVERRIDE_STATE = {
  employeeId: "",
  merchantId: "",
  startDate: null,
  endDate: null,
  cardType: "",
  fromAmount: "",
  toAmount: "",
  status: "",
  cardOrigin: "",
  gateway: "",
};

const ERROR_INIT_STATE = {
  startDate: false,
  endDate: false,
  invalidDateRange: false,
  tooLargeDateRange: false,
  fromAmount: false,
  toAmount: false,
  invalidAmountRange: false,
};

const OfflinePayments = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "OfflinePaymentsListGrid";
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isInitRef = useRef(true);

  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_CREATE_STATE(),
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [isIdInput, setIsIdInput] = useState(false);

  const [columnsArray, setColumnsArray] = useState([]);

  useEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setSearchFieldInput({
        ...searchFieldInput,
        employeeId: detailPageState[path].employeeId,
        merchantId: detailPageState[path].merchantId,
      });
    } else if (sessionState && sessionState[path]) {
      setSearchFieldInput({
        ...searchFieldInput,
        employeeId: sessionState[path].employeeId,
        merchantId: sessionState[path].merchantId,
      });
    }
  }, detailPageState[path]);

  useEffect(() => {
    if (searchFieldInput.id !== "" && !isIdInput) {
      setIsIdInput(true);
      setSearchFieldInput({
        ...searchFieldInput,
        ...INPUT_FIELD_ID_OVERRIDE_STATE,
      });
    } else if (searchFieldInput.id === "" && isIdInput) {
      setIsIdInput(false);
      setSearchFieldInput(INPUT_FIELD_CREATE_STATE());
    }
  }, [searchFieldInput.id]);

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    initialFetchList(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, []);

  const initialFetchList = async (getIsOudated) => {
    setLoading(true);
    const apiResponse = await queryList(
      trimObject({
        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
        ...INPUT_FIELD_CREATE_STATE(),
        ...(isInitRef.current && { initPage: "true" }),
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            isInitRef.current = false;
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_CREATE_STATE());
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = () => {
    isInitRef.current = true;
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            invalidDateRange: true,
          },
        (input) => {
          const startDate = moment(input.startDate).startOf("day");
          const endDate = moment(input.endDate).startOf("day");
          if (endDate.diff(startDate, "month", true) > 6) {
            return { tooLargeDateRange: true };
          }
        },
        (input) => isNaN(input.toAmount) && { toAmount: true },
        (input) => isNaN(input.fromAmount) && { fromAmount: true },
        (input) => input.fromAmount && !input.toAmount && { toAmount: true },
        (input) => input.toAmount && !input.fromAmount && { fromAmount: true },
        (input) =>
          input.fromAmount &&
          input.toAmount &&
          !isNaN(input.fromAmount) &&
          !isNaN(input.toAmount) &&
          parseFloat(input.fromAmount) > parseFloat(input.toAmount) && {
            invalidAmountRange: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryList(payload);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const payload = trimObject({ ...searchFieldInput });
    const apiResponse = await exportCsv(payload);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.export.function.success.message"),
              "success",
            );
            const url = responseBody.data;
            window.location = url;
          } else {
            snackbar(
              t("general.message.export.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
    setExporting(false);
  };

  // ===================== search field function start ===================== //
  const amountOnChange = (e, type, callback) => {
    if (type === "from") {
      setError({
        ...error,
        fromAmount: false,
        invalidAmountRange: false,
      });
      textInputOnChange(e, "fromAmount", callback);
    } else if (type === "to") {
      setError({ ...error, toAmount: false, invalidAmountRange: false });
      textInputOnChange(e, "toAmount", callback);
    } else {
      return;
    }
  };

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    if (field !== "id" && searchFieldInput.id !== "") return true;
  };

  const createdDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    let date = moment(rawDate).isValid()
      ? moment(rawDate)
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      date = moment(rawDate).isValid()
        ? date.startOf("day").toISOString()
        : date;
      setError({
        ...error,
        startDate: false,
        invalidDateRange: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      date = moment(rawDate).isValid() ? date.endOf("day").toISOString() : date;
      setError({ ...error, endDate: false, invalidDateRange: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    } else {
      return;
    }
  };
  // ===================== search field function end ===================== //

  // ===================== column display function ===================== //

  // ===================== column display function ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const navigateToDetail = (data) => {
    const { id } = data;
    const path = "/transactions/offline-payments-details";
    const payload = {
      name: transactions.find((tab) => tab.path === path).tabName + id,
      path: path,
      state: {
        id: id,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const columns = [
    {
      key: "id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    // {
    //   key: "category",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.category"),
    //   sorter: false,
    //   render: (data, key) => {
    //     return checkNonMoneyDataForTable(data, key);
    //   },
    // },
    {
      key: "created",
      title: t(PAGE_LOCALE_KEY + "columnTitle.transactionDate"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "merchantId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.merchantId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "cardType",
      title: t(PAGE_LOCALE_KEY + "columnTitle.cardType"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    // {
    //   key: "currency",
    //   title: t(PAGE_LOCALE_KEY + "columnTitle.transCurrency"),
    //   sorter: false,
    //   render: (data, key) => {
    //     return checkNonMoneyDataForTable(data, key); //TODO currency ISO code
    //   },
    // },
    {
      key: "total",
      title: t(PAGE_LOCALE_KEY + "columnTitle.total"),
      sorter: false,
      type: "amount",
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      key: "netAmount",
      title: t(PAGE_LOCALE_KEY + "columnTitle.net"),
      sorter: false,
      type: "amount",
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      key: "gateway",
      title: t(PAGE_LOCALE_KEY + "columnTitle.paymentGateway"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "trans_fee",
      title: t(PAGE_LOCALE_KEY + "columnTitle.smallTicketFee"),
      sorter: false,
      type: "amount",
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      key: "cardOrigin",
      title: t(PAGE_LOCALE_KEY + "columnTitle.cardOrigin"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "cardIssuingCountryCode",
      title: t(PAGE_LOCALE_KEY + "columnTitle.cardIssuingCode"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.id.label")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
            disabled={isInputDisabled("id")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.employeeId.label")}
            value={searchFieldInput.employeeId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "employeeId")}
            onfocusFn={() => textInputOnFocus("employeeId")}
            onblurFn={() => textInputOnBlur("employeeId")}
            sxParams={{ textFocus: focus.employeeId }}
            disabled={isInputDisabled("employeeId")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.merchantId.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
            disabled={isInputDisabled("merchantId")}
          />
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.startDate.label")}
            errorMessage={
              error.invalidDateRange
                ? t(PAGE_LOCALE_KEY + "search.startDate.errorMessage.2")
                : error.tooLargeDateRange
                  ? t(PAGE_LOCALE_KEY + "search.startDate.errorMessage.3")
                  : t(PAGE_LOCALE_KEY + "search.startDate.errorMessage.1")
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={createdDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate ||
                error.invalidDateRange ||
                error.tooLargeDateRange,
            }}
            disabled={isInputDisabled("startDate")}
          />
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "search.endDate.label")}
            errorMessage={
              error.invalidDateRange
                ? t(PAGE_LOCALE_KEY + "search.endDate.errorMessage.2")
                : error.tooLargeDateRange
                  ? t(PAGE_LOCALE_KEY + "search.endDate.errorMessage.3")
                  : t(PAGE_LOCALE_KEY + "search.endDate.errorMessage.1")
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={createdDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError:
                error.endDate ||
                error.invalidDateRange ||
                error.tooLargeDateRange,
            }}
            disabled={isInputDisabled("endDate")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.cardType.label")}
            selectionValue={searchFieldInput.cardType}
            onchangeFn={(e) => textInputOnChange(e, "cardType")}
            onfocusFn={() => textInputOnFocus("cardType")}
            onblurFn={() => textInputOnBlur("cardType")}
            options={cardTypeOptions("all")}
            sxParams={{ selectionFocus: focus.cardType }}
            disabled={isInputDisabled("cardType")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.fromAmount.label")}
            errorMessage={
              error.invalidAmountRange
                ? t(PAGE_LOCALE_KEY + "search.fromAmount.errorMessage.2")
                : t(PAGE_LOCALE_KEY + "search.fromAmount.errorMessage.1")
            }
            value={searchFieldInput.fromAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => amountOnChange(e, "from")}
            onfocusFn={() => textInputOnFocus("fromAmount")}
            onblurFn={() => textInputOnBlur("fromAmount")}
            sxParams={{
              textFocus: focus.fromAmount,
              textError: error.fromAmount || error.invalidAmountRange,
            }}
            disabled={isInputDisabled("fromAmount")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.toAmount.label")}
            errorMessage={
              error.invalidAmountRange
                ? t(PAGE_LOCALE_KEY + "search.toAmount.errorMessage.2")
                : t(PAGE_LOCALE_KEY + "search.toAmount.errorMessage.1")
            }
            value={searchFieldInput.toAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => amountOnChange(e, "to")}
            onfocusFn={() => textInputOnFocus("toAmount")}
            onblurFn={() => textInputOnBlur("toAmount")}
            sxParams={{
              textFocus: focus.toAmount,
              textError: error.toAmount || error.invalidAmountRange,
            }}
            disabled={isInputDisabled("toAmount")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.status.label")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnChange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={offlinePaymentStatusOptions("all")}
            sxParams={{ selectionFocus: focus.status }}
            disabled={isInputDisabled("status")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.cardOrigin.label")}
            selectionValue={searchFieldInput.cardOrigin}
            onchangeFn={(e) => textInputOnChange(e, "cardOrigin")}
            onfocusFn={() => textInputOnFocus("cardOrigin")}
            onblurFn={() => textInputOnBlur("cardOrigin")}
            options={cardOriginOperationOptions("all")}
            sxParams={{ selectionFocus: focus.cardOrigin }}
            disabled={isInputDisabled("cardOrigin")}
          />
          <Selection
            label={t(PAGE_LOCALE_KEY + "search.gateway.label")}
            selectionValue={searchFieldInput.gateway}
            onchangeFn={(e) => textInputOnChange(e, "gateway")}
            onfocusFn={() => textInputOnFocus("gateway")}
            onblurFn={() => textInputOnBlur("gateway")}
            options={paymentGatewayOptionsOperationOffline("all")}
            sxParams={{ selectionFocus: focus.gateway }}
            disabled={isInputDisabled("gateway")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading}
          exporting={exporting}
          exportBtn
          handleExport={handleExportCsv}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "transactions-offline-payments",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            // TODO column sorting
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(OfflinePayments);
